import { useRef, useState, useEffect } from 'react';
import { db, isConfigured } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

function Testimonials() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        if (!isConfigured) {
            console.log('Firebase not configured');
            setReviews([]);
            setLoading(false);
            return;
        }

        try {
            console.log('📊 Fetching approved reviews...');
            
            // Fetch only approved reviews
            const q = query(
                collection(db, 'reviews'),
                where('approved', '==', true)
            );

            
            const querySnapshot = await getDocs(q);
            console.log(`Found ${querySnapshot.size} approved reviews`);
            
            const reviewsList = querySnapshot.docs.map(doc => {
                const data = doc.data();
                console.log('Review data:', data);
                return {
                    id: doc.id,
                    ...data
                };
            });

            setReviews(reviewsList);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            setReviews([]);
        } finally {
            setLoading(false);
        }
    };

    const scrollLeft = () => {
        const container = scrollRef.current;
        if (!container || !container.children[0]) return;
        const cardWidth = container.children[0].offsetWidth;
        const gap = 8;
        const scrollDistance = cardWidth + gap;
        container.scrollBy({ left: -scrollDistance, behavior: 'smooth'});
    };

    const scrollRight = () => {
        const container = scrollRef.current;
        if (!container || !container.children[0]) return;
        const cardWidth = container.children[0].offsetWidth;
        const gap = 8;
        const scrollDistance = cardWidth + gap;
        container.scrollBy({ left: scrollDistance, behavior: 'smooth' });
    };

    const renderStars = (rating) => {
        const numStars = parseInt(rating) || 5;
        return (
            <div className='flex gap-1'>
                {[...Array(5)].map((_, index) => (
                    <svg
                        key={index}
                        className={`w-4 h-4 ${
                            index < numStars ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'
                        />
                    </svg>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <div className="text-white text-xl">Loading testimonials...</div>
            </div>
        );
    }

    return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center gap-8 sm:gap-12 lg:gap-16 px-4 sm:px-6 lg:px-8 py-8" id="testimonials">
            <h2 className='h-[15%] text-white font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-center'>
                TESTIMONIALS
            </h2>
            
            {reviews.length === 0 ? (
                <div className='text-white text-center'>
                    <p className='text-lg mb-4'>No testimonials yet.</p>
                    <a
                        href='/add-review'
                        className='inline-block bg-white text-[#FFB7CE] px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all'
                    >
                        Be the first to leave a review!
                    </a>
                </div>
            ) : (
                <div className='h-[80%] relative w-full max-w-7xl'>
                    {reviews.length > 1 && (
                        <button 
                            onClick={scrollLeft}
                            className='absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl font-bold transition-all duration-200 shadow-lg'
                            aria-label='Scroll left'
                        >
                            {"<"}
                        </button>
                    )}
                    
                    <div 
                        className='overflow-x-hidden overflow-y-hidden scroll-smooth'
                        ref={scrollRef}
                    >
                        <div className='flex gap-2 px-12'>
                            {reviews.map((item, index) => (
                                <div
                                    key={item.id || index} 
                                    className='flex-shrink-0 w-full sm:w-1/2 lg:w-1/3 px-4 py-6 bg-white text-gray-800 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg'
                                >
                                    <div className='space-y-3'>
                                        <div>
                                            <h3 className='font-bold text-lg mb-1'>{item.name}</h3>
                                            <h5 className='font-semibold text-sm text-gray-600 mb-2'>{item.company}</h5>
                                            {renderStars(item.ratings || item.rating)}
                                        </div>
                                        {item.projectTitle && (
                                            <div className='bg-[#FFB7CE] bg-opacity-10 px-3 py-1 rounded-full inline-block'>
                                                <p className='text-xs font-medium text-[#FFB7CE]'>Project: {item.projectTitle}</p>
                                            </div>
                                        )}
                                        <p className='text-sm leading-relaxed text-gray-700'>
                                            {item.review}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {reviews.length > 1 && (
                        <button 
                            onClick={scrollRight}
                            className='absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl font-bold transition-all duration-200 shadow-lg'
                            aria-label='Scroll right'
                        >
                            {">"}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Testimonials;